import { Link } from "react-router-dom";
import { BadgePercent, Loader2, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { formatIDR, pasangCopy } from "@kanata/shared";
import { Seo } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CartSuggestions } from "@/components/shop/CartSuggestions";
import { usePasangNotice } from "@/components/shop/PasangNotice";
import { PromoBlock } from "@/components/shop/PromoBlock";
import { Separator } from "@/components/ui/separator";
import type { CartLine, CartUpgrade } from "@/lib/api";
import { useCart } from "@/lib/cart";
import {
  cartRows,
  lineQtyChange,
  removeSourceChange,
  upgradePasangTargets,
  upgradeQtyChanges,
} from "@/lib/cart-upgrade";

export default function Cart() {
  const { cart, loading, error } = useCart();

  const items = cart?.items ?? [];
  const rows = cartRows(cart);
  const empty = !loading && items.length === 0;
  const saving = (cart?.compareAtSubtotalIdr ?? 0) - (cart?.subtotalIdr ?? 0);
  const listSaving = cart?.listSavingIdr ?? 0;

  return (
    <>
      <Seo title="Keranjang — Kanata Indonesia" description="Isi keranjang belanja kamu." noindex />

      <main className="min-h-[70vh] pt-28 sm:pt-36">
        <div className="mx-auto max-w-5xl px-5 pb-20 sm:px-8">
          <h1 className="text-3xl font-medium tracking-tight text-ink sm:text-4xl">Keranjang</h1>

          {loading && (
            <div className="flex items-center gap-2 py-16 text-ink/50">
              <Loader2 className="size-4 animate-spin" />
              Memuat keranjang…
            </div>
          )}

          {error && !loading && (
            <p className="mt-6 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </p>
          )}

          {empty && (
            <div className="py-16">
              <div className="flex size-14 items-center justify-center rounded-full bg-cream">
                <ShoppingBag className="size-6 text-ink/35" />
              </div>
              <p className="mt-5 text-lg text-ink/70">Belum ada apa-apa di sini.</p>
              <Button asChild size="lg" className="mt-6">
                <Link to="/katalog">Lihat semua meja</Link>
              </Button>
            </div>
          )}

          {items.length > 0 && (
            <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_20rem] lg:gap-14">
              <div>
                <ul className="divide-y divide-ink/8">
                  {rows.map((row) =>
                    row.kind === "upgrade" ? (
                      <UpgradeRow key={`up-${row.upgrade.skuId}`} upgrade={row.upgrade} />
                    ) : (
                      <LineRow key={row.line.skuId} line={row.line} qty={row.qty} />
                    ),
                  )}
                </ul>
                {cart?.promo && <PromoBlock promo={cart.promo} />}
              </div>

              <aside className="lg:sticky lg:top-28 lg:self-start">
                <div className="rounded-2xl border border-ink/8 bg-cream/60 p-6">
                  <h2 className="font-medium text-ink">Ringkasan</h2>

                  {listSaving > 0 && (
                    <div className="mt-4 flex justify-between text-sm">
                      <span className="text-ink/60">Hemat dari harga normal</span>
                      <span className="font-medium text-sale">−{formatIDR(listSaving)}</span>
                    </div>
                  )}
                  {saving > 0 && (
                    <div className={`${listSaving > 0 ? "mt-2" : "mt-4"} flex justify-between text-sm`}>
                      <span className="text-ink/60">Hemat harga paket</span>
                      <span className="font-medium text-brand-dark">−{formatIDR(saving)}</span>
                    </div>
                  )}
                  <div className={`${saving > 0 || listSaving > 0 ? "mt-2" : "mt-4"} flex justify-between text-sm`}>
                    <span className="text-ink/60">Subtotal</span>
                    <span className="font-medium text-ink">{formatIDR(cart?.subtotalIdr ?? 0)}</span>
                  </div>
                  {cart?.promo && cart.promo.savingIdr > 0 && (
                    <div className="mt-2 flex justify-between text-sm">
                      <span className="text-ink/60">{cart.promo.name}</span>
                      <span className="font-medium text-brand-dark">Gratis</span>
                    </div>
                  )}
                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-ink/60">Ongkos kirim</span>
                    {/* Deliberately not estimated. The real number comes from
                        the courier once we know the address, and a placeholder
                        here would be a figure the customer then sees change. */}
                    <span className="text-ink/50">Dihitung saat checkout</span>
                  </div>
                  {cart?.promo && cart.promo.shippingSubsidyMaxIdr > 0 && (
                    <p className="mt-1 text-right text-xs text-brand-dark">
                      Subsidi s/d {formatIDR(cart.promo.shippingSubsidyMaxIdr)} dipotong saat checkout
                    </p>
                  )}

                  <Separator className="my-4" />

                  <div className="flex justify-between">
                    <span className="font-medium text-ink">Total sementara</span>
                    <span className="text-lg font-medium text-ink">
                      {formatIDR(cart?.subtotalIdr ?? 0)}
                    </span>
                  </div>

                  <Button asChild size="lg" className="mt-5 w-full">
                    <Link to="/checkout">Lanjut ke checkout</Link>
                  </Button>
                  {/* Adding to the cart lands here, so the way back to the
                      products has to be as plain as the way forward. */}
                  <Button asChild size="lg" variant="outline" className="mt-2 w-full">
                    <Link to="/katalog">Lanjut belanja</Link>
                  </Button>

                  <p className="mt-3 text-center text-xs leading-relaxed text-ink/50">
                    Ongkir dihitung dari alamat kamu di langkah berikutnya.
                  </p>
                </div>
              </aside>
            </div>
          )}

          {items.length > 0 && <CartSuggestions cart={cart} />}
        </div>
      </main>
    </>
  );
}

function Stepper({ qty, onChange, disabled }: { qty: number; onChange: (qty: number) => void; disabled: boolean }) {
  return (
    <div className="flex items-center rounded-full border border-ink/12">
      <button
        type="button"
        aria-label="Kurangi jumlah"
        disabled={disabled}
        onClick={() => onChange(qty - 1)}
        className="flex size-9 items-center justify-center rounded-l-full text-ink/60 transition-colors hover:bg-ink/[0.04] disabled:opacity-40"
      >
        <Minus className="size-3.5" />
      </button>
      <span className="w-9 text-center text-sm font-medium tabular-nums">{qty}</span>
      <button
        type="button"
        aria-label="Tambah jumlah"
        disabled={disabled}
        onClick={() => onChange(qty + 1)}
        className="flex size-9 items-center justify-center rounded-r-full text-ink/60 transition-colors hover:bg-ink/[0.04] disabled:opacity-40"
      >
        <Plus className="size-3.5" />
      </button>
    </div>
  );
}

function variantText(v: { size?: string; topColor?: string; legColor?: string }) {
  return [v.size ? `${v.size} cm` : null, v.topColor, v.legColor && `kaki ${v.legColor}`]
    .filter(Boolean)
    .join(" · ");
}

/**
 * A line as the customer chose it, showing only what an upgrade has not
 * already taken. Its controls write the stored quantity, upgraded units
 * included, because that is the number the cart keeps.
 */
function LineRow({ line, qty }: { line: CartLine; qty: number }) {
  const { busy, applyQty, setPasang } = useCart();
  const pasangNotice = usePasangNotice();
  const setRemaining = (n: number) => void applyQty([lineQtyChange(line, n)]);

  return (
    <li className="flex gap-4 py-5">
      <Link to={`/produk/${line.productSlug}`} className="size-24 shrink-0 overflow-hidden rounded-xl bg-cream">
        {line.imageUrl && <img src={line.imageUrl} alt={line.productName} className="size-full object-cover" />}
      </Link>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Link
              to={`/produk/${line.productSlug}`}
              className="font-medium text-ink transition-colors hover:text-brand"
            >
              {line.productName}
            </Link>
            <p className="mt-0.5 text-sm text-ink/55">{variantText(line)}</p>
          </div>
          <div className="shrink-0 text-right">
            <p className="font-medium text-ink">{formatIDR(line.lineTotalIdr)}</p>
            {line.listPriceIdr && qty > 0 ? (
              <del className="text-sm text-ink/45">{formatIDR(line.listPriceIdr * qty)}</del>
            ) : null}
          </div>
        </div>

        {line.pasangEligible && (
          <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-ink/70">
            <Checkbox
              checked={line.pasang}
              disabled={busy}
              onCheckedChange={(v) => {
                void setPasang(line.skuId, v === true);
                if (v === true) pasangNotice.show({ productNames: [line.productName] });
              }}
            />
            {pasangCopy.label}
            <span className="text-ink/45">— {pasangCopy.fee.toLowerCase()}</span>
          </label>
        )}

        <div className="mt-3 flex items-center gap-3">
          <Stepper qty={qty} onChange={setRemaining} disabled={busy} />
          <button
            type="button"
            disabled={busy}
            onClick={() => setRemaining(0)}
            className="inline-flex items-center gap-1.5 text-sm text-ink/50 transition-colors hover:text-destructive disabled:opacity-40"
          >
            <Trash2 className="size-3.5" />
            Hapus
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * Lines the cart is charging as a package — Klasik + Raya as Plus. Shown as
 * the product they became, with what went into it and why the price changed,
 * so the customer reads the reason instead of discovering a different number.
 */
function UpgradeRow({ upgrade }: { upgrade: CartUpgrade }) {
  const { cart, busy, applyQty, setPasangMany } = useCart();
  const pasangNotice = usePasangNotice();
  if (!cart) return null;
  const [primary, ...rest] = upgrade.sources;

  return (
    <li className="py-5">
      <div className="flex gap-4">
        <Link
          to={upgrade.productSlug ? `/produk/${upgrade.productSlug}` : "#"}
          className="size-24 shrink-0 overflow-hidden rounded-xl bg-cream"
        >
          {upgrade.imageUrl && (
            <img src={upgrade.imageUrl} alt={upgrade.productName} className="size-full object-cover" />
          )}
        </Link>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={upgrade.productSlug ? `/produk/${upgrade.productSlug}` : "#"}
                  className="font-medium text-ink transition-colors hover:text-brand"
                >
                  {upgrade.productName}
                </Link>
                <Badge>Harga paket</Badge>
              </div>
              <p className="mt-0.5 text-sm text-ink/55">{variantText(upgrade)}</p>
            </div>
            <div className="shrink-0 text-right">
              <p className="font-medium text-ink">{formatIDR(upgrade.lineTotalIdr)}</p>
              {/* The real price of the lines bought separately — the same
                  compare-at the package PDP shows — not an invented "was". */}
              <p className="text-xs text-ink/45">
                <span className="sr-only">Harga jika dibeli terpisah: </span>
                <del>{formatIDR(upgrade.compareAtIdr * upgrade.qty)}</del>
              </p>
            </div>
          </div>

          <ul className="mt-2 space-y-1 text-sm text-ink/60">
            {primary && <li>Berisi {primary.label}</li>}
            {rest.map((s) => (
              <li key={s.skuId} className="flex items-center gap-2">
                <span>+ {s.label}</span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void applyQty([removeSourceChange(cart, upgrade, s.skuId)])}
                  aria-label={`Hapus ${s.productName}`}
                  className="inline-flex items-center gap-1 text-xs text-ink/45 transition-colors hover:text-destructive disabled:opacity-40"
                >
                  <X className="size-3" />
                  Hapus
                </button>
              </li>
            ))}
          </ul>

          {upgrade.pasangEligible && (
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-ink/70">
              <Checkbox
                checked={upgrade.pasang}
                disabled={busy}
                onCheckedChange={(v) => {
                  void setPasangMany(upgradePasangTargets(cart, upgrade), v === true);
                  if (v === true) pasangNotice.show({ productNames: [upgrade.productName] });
                }}
              />
              {pasangCopy.label}
              <span className="text-ink/45">— {pasangCopy.fee.toLowerCase()}</span>
            </label>
          )}

          <div className="mt-3 flex items-center gap-3">
            <Stepper
              qty={upgrade.qty}
              onChange={(n) => void applyQty(upgradeQtyChanges(cart, upgrade, n - upgrade.qty))}
              disabled={busy}
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => void applyQty(upgradeQtyChanges(cart, upgrade, -upgrade.qty))}
              className="inline-flex items-center gap-1.5 text-sm text-ink/50 transition-colors hover:text-destructive disabled:opacity-40"
            >
              <Trash2 className="size-3.5" />
              Hapus
            </button>
          </div>
        </div>
      </div>

      <p
        role="status"
        className="mt-4 flex items-start gap-2 rounded-xl bg-brand-light/60 px-4 py-3 text-sm text-brand-dark"
      >
        <BadgePercent className="mt-0.5 size-4 shrink-0" />
        {upgrade.notice}
      </p>
    </li>
  );
}
